import { gameLogger } from '../../utils/GameLogger';
import { TextureGenerator } from '../utils/rendering/TextureGenerator';
import { SpriteTextureSystem } from '../systems/sprite/SpriteTextureSystem';
import { generateMobSVG } from '../rendering/mobs/MobSVGRegistry';
import type { IAssets } from './IAssets';
import type { AssetManifest, AssetEntry } from './AssetManifest';

// Phase H10 Arc λ.4 (2026-05-09): AssetLoader.preload now takes an
// `IAssets` (substrate-agnostic) instead of a Phaser.Scene. The boot
// manifest is built procedurally from the SVG generators below; future
// static-asset pipelines can append entries to this list without
// touching AssetLoader internals.

/**
 * AssetLoader
 * Central management for loading and generating game assets.
 * Ensures all required textures are available in the substrate-agnostic
 * cache (IAssets) before the game starts.
 */
export class AssetLoader {
  private static instance: AssetLoader;
  private textureSystem: SpriteTextureSystem | null = null;

  private constructor() { }

  public static getInstance(): AssetLoader {
    if (!AssetLoader.instance) {
      AssetLoader.instance = new AssetLoader();
    }
    return AssetLoader.instance;
  }

  /**
   * Build the boot-time asset manifest. Pure function — generators run
   * once per call so callers can decide when payloads are produced.
   */
  public async buildManifest(): Promise<AssetManifest> {
    const manifest: AssetManifest = [];

    // 1. Mobs — preload from MobSVGRegistry (same source MobRenderer uses)
    const mobTypes = ['pig', 'pig_mom', 'snail', 'slime', 'mushroom',
      'wolf', 'boss_wolf', 'bat', 'cave_spider', 'crystal_golem', 'soul_flame', 'training_dummy'];
    for (const mobType of mobTypes) {
      const svg = await generateMobSVG(mobType);
      if (svg) manifest.push(AssetLoader.entry(`mob_${mobType}`, svg));
    }
    manifest.push(AssetLoader.entry('dummy_tex', TextureGenerator.generateTrainingDummySVG()));

    // 2. NPCs - Ghost appearances
    manifest.push(AssetLoader.entry('npc_guide', TextureGenerator.generateGhostSVG({
      color: '#ffffff',
      eyes: 'happy',
      mouth: 'smile',
      shape: 'classic',
      aura: 'none',
      accessory: 'hat',
    })));

    manifest.push(AssetLoader.entry('npc_merchant', TextureGenerator.generateGhostSVG({
      color: '#ffd700',
      eyes: 'round',
      mouth: 'neutral',
      shape: 'classic',
      aura: 'none',
      accessory: 'bow',
    })));

    manifest.push(AssetLoader.entry('npc_default', TextureGenerator.generateGhostSVG(
      TextureGenerator.generateRandomAppearance()
    )));

    // 3. Items
    manifest.push(AssetLoader.entry('item_health_potion', AssetLoader.placeholderSVG('#ff0000', 'HP')));
    manifest.push(AssetLoader.entry('item_mana_potion', AssetLoader.placeholderSVG('#0000ff', 'MP')));
    manifest.push(AssetLoader.entry('item_gold', AssetLoader.placeholderSVG('#ffd700', '$$')));
    manifest.push(AssetLoader.entry('item_weapon', AssetLoader.placeholderSVG('#888888', '⚔️')));

    // 4. Props / Environment
    manifest.push(AssetLoader.entry('prop_palm_tree', TextureGenerator.generatePalmTreeSVG()));
    manifest.push(AssetLoader.entry('prop_shell', TextureGenerator.generateShellSVG()));
    manifest.push(AssetLoader.entry('prop_starfish', TextureGenerator.generateStarfishSVG()));

    return manifest;
  }

  /**
   * Async preload through IAssets. Loads every entry in the manifest in
   * parallel and resolves once each texture is in the cache.
   *
   * Phase H10 Arc λ.4 — replaces the prior `preload(scene)` + scene.load.image
   * batched-loader pattern.
   * Phase H10 Arc λ.9 Stage B4 phase 2 — second arg accepts EITHER a
   * Phaser.Scene (legacy, wraps in PhaserAssets internally via SpriteTextureSystem)
   * OR omitted (substrate-agnostic — SpriteTextureSystem reuses the IAssets
   * passed as the first arg). Once every caller drops the scene arg this
   * second parameter retires.
   */
  public async preload(assets: IAssets, scene?: unknown): Promise<void> {
    gameLogger.info('assets', '🎨 Starting Asset Preload (IAssets)...');

    // Phase H10 Arc λ.9 Stage B4 phase 3 — initialize SpriteTextureSystem
    // with whichever input the caller has. PreloadScene now passes a
    // PixiAssets; legacy callers may pass a Phaser.Scene.
    this.textureSystem = SpriteTextureSystem.getInstance(scene ?? assets);

    const manifest = await this.buildManifest();
    await assets.loadBatch(manifest);

    gameLogger.info('assets', `✅ Asset Preload Complete (${manifest.length} entries)`);
  }

  /**
   * Get the texture system instance
   */
  public getTextureSystem(): SpriteTextureSystem | null {
    return this.textureSystem;
  }

  /**
   * Critical-texture sanity check. Reads through IAssets so PhaserAssets
   * + PixiAssets both work.
   */
  public areCriticalTexturesLoaded(assets: IAssets): boolean {
    const critical = [
      'pig_sprite',
      'dummy_tex',
      'fallback_player',
      'fallback_mob',
      'fallback_npc',
    ];

    for (const key of critical) {
      if (!assets.hasTexture(key) && !assets.hasTexture('mob_pig')) {
        gameLogger.warn('assets', `Critical texture missing: ${key}`);
        return false;
      }
    }
    return true;
  }

  /**
   * Get a list of missing textures (substrate-agnostic).
   */
  public getMissingTextures(assets: IAssets): string[] {
    const expected = [
      'pig_sprite',
      'mob_snail',
      'mob_slime',
      'dummy_tex',
      'npc_guide',
      'npc_merchant',
      'npc_default',
      'item_health_potion',
      'item_mana_potion',
      'item_gold',
      'item_weapon',
      'prop_palm_tree',
      'prop_shell',
      'prop_starfish',
      'fallback_player',
      'fallback_mob',
      'fallback_npc',
      'fallback_item',
      'fallback_prop',
    ];

    return expected.filter(key => !assets.hasTexture(key));
  }

  // ----- helpers -----

  private static entry(key: string, dataUri: string): AssetEntry {
    return { key, dataUri };
  }

  /** Simple placeholder SVG for items lacking specific art. */
  private static placeholderSVG(color: string, text: string): string {
    const svg = `
    <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="28" height="28" rx="8" fill="${color}" stroke="white" stroke-width="2"/>
      <text x="16" y="22" font-family="Arial" font-size="14" fill="white" text-anchor="middle" font-weight="bold">${text}</text>
    </svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)));
  }
}
